import { PROJECTOR_CONFIG } from './config';
import { ProceduralPoolGrid, PROC_SURFACE_IDS } from '../../../../Pool/logic/ProceduralPoolGrid';

/**
 * Все четыре стены чаши (для равномерного периметрального размещения).
 */
const FOUR_WALLS = [
    PROC_SURFACE_IDS.WALL_FRONT,
    PROC_SURFACE_IDS.WALL_BACK,
    PROC_SURFACE_IDS.WALL_LEFT,
    PROC_SURFACE_IDS.WALL_RIGHT
];

/**
 * @param {unknown} poolType
 * @returns {boolean}
 */
function isOverflowPoolType(poolType) {
    const t = String(poolType || '').toLowerCase();
    return t === 'переливной' || t === 'overflow' || t.includes('перелив');
}

/**
 * Рассчитать количество прожекторов на основе ПЕРИМЕТРА
 */
export const calculateProjectorQuantity = (length, width) => {
    const perimeter = (length + width) * 2;
    const step = 4.5;
    let count = Math.round(perimeter / step);

    if (count < 2) count = 2;
    if (count % 2 !== 0) count += 1;

    return count;
};

/**
 * Вспомогательная функция для распределения прожекторов по группе стен
 */
const distributeOnWalls = (gridSystem, walls, totalQuantity, idPrefix, startIndex) => {
    const slots = [];
    let currentIdIndex = startIndex;
    let remaining = totalQuantity;

    walls.forEach((surfaceId, index) => {
        const countOnWall = Math.ceil(remaining / (walls.length - index));
        remaining -= countOnWall;

        if (countOnWall > 0) {
            const options = {
                padding: 0,
                offset: PROJECTOR_CONFIG.POSITIONING.WALL_OFFSET || 0.007,
            };

            const wallSlots = gridSystem.distributeEvenly(surfaceId, countOnWall, options);

            wallSlots.forEach(slot => {
                slots.push({
                    ...slot,
                    id: `${idPrefix}-${currentIdIndex++}`,
                    side: surfaceId
                });
            });
        }
    });

    return { slots, nextIndex: currentIdIndex };
};

/**
 * Равномерно распределить N прожекторов по всем 4 стенам (по возможности поровну на каждую).
 */
const distributeEvenlyOnFourWalls = (gridSystem, totalQuantity, idPrefix, startIndex) => {
    const slots = [];
    let idCounter = startIndex;
    const base = Math.floor(totalQuantity / 4);
    let rem = totalQuantity - base * 4;

    const options = {
        padding: 0,
        offset: PROJECTOR_CONFIG.POSITIONING.WALL_OFFSET || 0.007,
    };

    for (let i = 0; i < 4; i++) {
        const countOnWall = base + (i < rem ? 1 : 0);
        if (countOnWall <= 0) continue;

        const surfaceId = FOUR_WALLS[i];
        const wallSlots = gridSystem.distributeEvenly(surfaceId, countOnWall, options);

        for (const slot of wallSlots) {
            slots.push({
                ...slot,
                id: `${idPrefix}-${idCounter++}`,
                side: surfaceId
            });
        }
    }

    return { slots, nextIndex: idCounter };
};

/**
 * Распределить N прожекторов по 4 стенам пропорционально длине каждой стены.
 * @param {import('../../../../Pool/logic/ProceduralPoolGrid').ProceduralPoolGrid} gridSystem
 * @param {number} totalQuantity
 * @param {string} idPrefix
 * @param {number} startIndex
 */
function distributeProportionalOnFourWalls(gridSystem, totalQuantity, idPrefix, startIndex) {
    const slots = [];
    let idCounter = startIndex;

    const wallSizes = FOUR_WALLS.map((surfaceId) => {
        const surface = gridSystem.surfaces[surfaceId];
        return Math.max(0.1, Number(surface?.sizeMain) || 0.1);
    });

    const sumLen = wallSizes.reduce((acc, len) => acc + len, 0);
    const quotas = wallSizes.map((len) => (totalQuantity * len) / sumLen);
    const counts = quotas.map((q) => Math.floor(q));
    let remainder = totalQuantity - counts.reduce((acc, n) => acc + n, 0);

    const byFraction = quotas
        .map((q, i) => ({ i, frac: q - Math.floor(q) }))
        .sort((a, b) => b.frac - a.frac);

    for (let k = 0; k < remainder; k++) {
        counts[byFraction[k % byFraction.length].i] += 1;
    }

    const options = {
        padding: 0,
        offset: PROJECTOR_CONFIG.POSITIONING.WALL_OFFSET || 0.007,
    };

    for (let i = 0; i < FOUR_WALLS.length; i++) {
        const countOnWall = counts[i];
        if (countOnWall <= 0) continue;

        const surfaceId = FOUR_WALLS[i];
        const wallSlots = gridSystem.distributeEvenly(surfaceId, countOnWall, options);

        for (const slot of wallSlots) {
            slots.push({
                ...slot,
                id: `${idPrefix}-${idCounter++}`,
                side: surfaceId,
            });
        }
    }

    return { slots, nextIndex: idCounter };
}

/**
 * Основная функция расчета СЛОТОВ (позиций)
 * @param {object} poolDimensions - { length, width, depth }
 * @param {number|null} quantity - Принудительное количество
 * @param {string} poolType - Тип бассейна
 * @returns {Array} Массив слотов { x, y, z, rotation, side, id }
 */
export const calculateProjectorSlots = (poolDimensions, quantity = null, poolType = 'скиммерный') => {
    const { length, width, depth } = poolDimensions;
    const totalQuantity = quantity || calculateProjectorQuantity(length, width);

    if (totalQuantity <= 0) return [];

    const gridSystem = new ProceduralPoolGrid(length, width, depth, poolType);

    const isLengthLonger = length >= width;
    // LEFT/RIGHT — вдоль длины (Z), FRONT/BACK — вдоль ширины (X)
    const lengthWalls = [PROC_SURFACE_IDS.WALL_LEFT, PROC_SURFACE_IDS.WALL_RIGHT];
    const widthWalls = [PROC_SURFACE_IDS.WALL_FRONT, PROC_SURFACE_IDS.WALL_BACK];

    // Прожекторы всегда на длинной стороне бассейна (независимо от типа).
    const mainWalls = isLengthLonger ? lengthWalls : widthWalls;

    if (totalQuantity <= 32) {
        const { slots } = distributeOnWalls(
            gridSystem,
            mainWalls,
            totalQuantity,
            'proj-main',
            0
        );
        return slots;
    }

    // Много прожекторов — по всем 4 стенам
    const isOverflow = isOverflowPoolType(poolType);
    const { slots } = isOverflow
        ? distributeProportionalOnFourWalls(gridSystem, totalQuantity, 'proj', 0)
        : distributeEvenlyOnFourWalls(gridSystem, totalQuantity, 'proj', 0);
    return slots;
};

export const calculateProjectorPositions = calculateProjectorSlots;
